import React, { useEffect, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography } from "@mui/material";
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from '../../component/addCategoryForm/addCategoryForm.module.css';
import { closeCircle, UploadImg } from '../../Svg';
import styless from '../../component/scheduleComps/scheduleList/partList.module.css';
import NestedUserHeader from "../../component/reusable/NestedUserHeader";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from 'yup';
import { toast } from "react-toastify";
import axios from "axios";
import api from "../../utils/api";
import { addLoginBuilderThunk, getLoginBuilderThunk } from "../../redux/appearanceSlice";

const Accordions = styled(Accordion)(({ theme }) => ({
  borderRadius: '8px !important',
  boxShadow: 'none',
  border: `1px solid ${theme.palette.divider}`,
  margin: '20px 0',
  '&::before': { background: 'transparent', content: 'none' },
}));

const defaultLoginSlides = [
  { image: '/assets/login.png', title: 'Thomas Edison', description: 'From pioneering electric light to shaping the future of innovation.' },
  { image: '/assets/login-slide-components.png', title: 'Built for precision', description: 'Source the right electronic components with clarity and confidence.' },
  { image: '/assets/login-slide-precision.png', title: 'Ideas into production', description: 'Bring every design to life with reliable sourcing in one place.' },
];

const getInitialSlides = (loginBanner) => {
  const savedSlides = loginBanner?.slides?.filter((slide) => slide?.image);
  if (savedSlides?.length) {
    const preparedSlides = savedSlides
      .slice(0, 3)
      .map((slide, index) => ({ ...defaultLoginSlides[index], ...slide }));

    return [
      ...preparedSlides,
      ...defaultLoginSlides.slice(preparedSlides.length),
    ];
  }

  return [
    { ...defaultLoginSlides[0], image: loginBanner?.image || defaultLoginSlides[0].image },
    ...defaultLoginSlides.slice(1),
  ];
};

const LoginPage = () => {
  const [expanded, setExpanded] = useState('panel1');
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const dispatch = useDispatch();
  const imageRefs = useRef([]);
  const { login } = useSelector((state) => state.appearances);

  useEffect(() => {
    dispatch(getLoginBuilderThunk());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: { slides: getInitialSlides(login?.data) },
    enableReinitialize: true,
    validationSchema: Yup.object({
      slides: Yup.array().of(Yup.object({
        image: Yup.string().required('Slide image is required'),
        title: Yup.string().trim().required('Slide title is required'),
        description: Yup.string().trim().required('Short description is required'),
      })).min(1, 'At least one slide is required').max(3, 'Only three login slides are supported'),
    }),
    onSubmit: async (values, { resetForm }) => {
      const slides = values.slides.filter((slide) => slide.image);
      try {
        await dispatch(addLoginBuilderThunk({ slides, image: slides[0]?.image })).unwrap();
        resetForm({ values: { slides } });
        dispatch(getLoginBuilderThunk());
      } catch (error) {
        // The thunk displays the failure message and leaves the form values untouched.
      }
    },
  });

  const handleImageChange = async (event, index) => {
    const file = event.target?.files?.[0] || event.dataTransfer?.files?.[0];
    if (!file) return;

    setUploadingIndex(index);
    try {
      const body = { key: `${Date.now()}_${file.name}`, fileType: file.type, fileName: file.name };
      const { data, status } = await api.getPutSignedUrl(body);
      if (status === 200) {
        await axios.put(data.data?.preSigned, file, { headers: { "Content-Type": file.type } });
        formik.setFieldValue(`slides[${index}].image`, data?.data?.url);
      }
    } catch (error) {
      console.log(error);
      toast.error('Error uploading image');
    } finally {
      setUploadingIndex(null);
      event.target.value = '';
    }
  };

  const restoreSlide = (index) => formik.setFieldValue(`slides[${index}]`, defaultLoginSlides[index]);
  const errorsFor = (index, field) => formik.touched.slides?.[index]?.[field] && formik.errors.slides?.[index]?.[field];
  const handleCancel = () => {
    formik.resetForm({ values: { slides: getInitialSlides(login?.data) } });
    toast.info('Unsaved changes discarded');
  };

  return (
    <>
      <NestedUserHeader index="Login Page Builder" root="Dashboard" buttonText="Back to list" />
      <Box sx={{ width: 'min(100%, 980px)' }}>
        <Accordions expanded={expanded === 'panel1'} onChange={(_, isExpanded) => setExpanded(isExpanded ? 'panel1' : false)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography sx={{ fontSize: 16, color: '#1D1F2C', fontWeight: 700 }}>Login slider</Typography>
              <Typography sx={{ fontSize: 13, color: '#667085', mt: 0.35 }}>Manage all three background images, titles and short descriptions.</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ background: '#F0F1F3B2', margin: '0 20px 20px', padding: 2.5 }}>
            <form onSubmit={formik.handleSubmit}>
              <Box sx={{ display: 'grid', gap: 2 }}>
                {formik.values.slides.map((slide, index) => (
                  <Box key={`login-slide-${index}`} sx={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 2, p: 2.5 }}>
                    <Typography sx={{ fontWeight: 700, color: '#1D1F2C', mb: 2 }}>Slide {index + 1}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '200px 1fr' }, gap: 2.5, alignItems: 'start' }}>
                      <Box>
                        <Box
                          className={styles.uploadBox}
                          sx={{ minHeight: 132, background: '#F8FAFC !important', position: 'relative', overflow: 'hidden', cursor: 'pointer' }}
                          onClick={() => imageRefs.current[index]?.click()}
                        >
                          {slide.image ? <>
                            <img src={slide.image} alt={`Login slide ${index + 1}`} className={styles.previewImage} style={{ width: '100%', height: 132, objectFit: 'cover' }} />
                            <div className={styles.cancel_cross} onClick={(event) => { event.stopPropagation(); restoreSlide(index); }} title="Restore default image">{closeCircle()}</div>
                          </> : <>
                            <span className={styles.uploadIcon}><UploadImg /></span>
                            <p className={styles.addCatPara}>Upload image</p>
                          </>}
                          <input ref={(element) => { imageRefs.current[index] = element; }} type="file" accept="image/*" style={{ display: 'none' }} onChange={(event) => handleImageChange(event, index)} />
                        </Box>
                        <Button type="button" size="small" variant="text" onClick={() => imageRefs.current[index]?.click()} disabled={uploadingIndex === index} sx={{ mt: 0.5, textTransform: 'none' }}>
                          {uploadingIndex === index ? 'Uploading…' : 'Replace image'}
                        </Button>
                        {errorsFor(index, 'image') && <Typography color="error" variant="caption" display="block">{errorsFor(index, 'image')}</Typography>}
                      </Box>
                      <Box sx={{ display: 'grid', gap: 1.75 }}>
                        <label className={styles.addCatLabel}>
                          Slide title
                          <input className={styles.addCatInput} name={`slides[${index}].title`} value={slide.title} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                          {errorsFor(index, 'title') && <span className={styles.errorText}>{errorsFor(index, 'title')}</span>}
                        </label>
                        <label className={styles.addCatLabel}>
                          Short description
                          <textarea className={styles.addCatTextarea} name={`slides[${index}].description`} value={slide.description} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                          {errorsFor(index, 'description') && <span className={styles.errorText}>{errorsFor(index, 'description')}</span>}
                        </label>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <button type="button" className={styless.cancelBtn} onClick={handleCancel} disabled={formik.isSubmitting}>Cancel</button>
                <button type="submit" className={styless.submitBtn} disabled={formik.isSubmitting}>{formik.isSubmitting ? 'Please wait...' : 'Save changes'}</button>
              </Box>
            </form>
          </AccordionDetails>
        </Accordions>
      </Box>
    </>
  );
};

export default LoginPage;
